import React, { useState, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import BottomNav from '@/components/BottomNav';
import { Cross, Users, ShoppingBag, BookOpen, Megaphone, MessageSquare, Clock, Sparkles, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import SuiteChatPanel from '@/components/SuiteChatPanel';
import PresencePanel from '@/components/PresencePanel';
import { MeetSection, MediaSection, MasterySection } from './BuddhistSuitePage';

// Same 5 tabs as Buddhist, adapted for Katolik category.
const TABS = [
  { key: 'meet',    icon: Users,        id: 'Katolik Meet',    en: 'Katolik Meet',    desc_id: 'Buat & daftar acara komunitas',           desc_en: 'Create & register for events' },
  { key: 'chat',    icon: MessageSquare, id: 'Katolik Chat',   en: 'Katolik Chat',   desc_id: 'Grup wilayah — chat, foto, video, polling', desc_en: 'Regional groups — chat, media, polls' },
  { key: 'mart',    icon: ShoppingBag,  id: 'Katolik Mart',    en: 'Katolik Mart',    desc_id: 'Jual-beli & lowongan kerja',              desc_en: 'Marketplace & job listings' },
  { key: 'mastery', icon: BookOpen,     id: 'Katolik Mastery', en: 'Katolik Mastery', desc_id: 'E-book & forum Q&A',                       desc_en: 'Resource library & Q&A' },
  { key: 'media',   icon: Megaphone,    id: 'Katolik Media',   en: 'Katolik Media',   desc_id: 'Paste broadcast → analisa AI',             desc_en: 'Paste broadcast → AI analysis' },
];

function ComingSoon({ label, lang }) {
  return (
    <div className="glass-card rounded-2xl p-8 text-center" data-testid={`katolik-${label.toLowerCase().replace(/\s+/g, '-')}-placeholder`}>
      <Clock className="w-10 h-10 text-[#D98A94] mx-auto mb-3 opacity-60" />
      <div className="font-bold t-text mb-1">{label}</div>
      <p className="text-sm t-text-muted">
        {lang === 'en'
          ? 'Coming next release. Full parity with Buddhist Suite is being ported over.'
          : 'Segera hadir. Fitur ini sedang di-port agar sama persis dengan Buddhist Suite.'}
      </p>
    </div>
  );
}

export default function KatolikSuitePage() {
  const { API, token, member, claimedProfile } = useAuth();
  const { lang } = useLang();
  const [tab, setTab] = useState('chat');
  const isKatolik = claimedProfile?.category === 'Katholik Match';
  const isID = lang !== 'en';
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  if (!isKatolik) {
    return (
      <div className="min-h-screen t-bg flex items-center justify-center px-6">
        <div className="glass-card rounded-3xl p-8 max-w-md text-center">
          <Cross className="w-12 h-12 text-[#D98A94] mx-auto mb-4" />
          <h2 className="text-xl font-bold t-text mb-2">Katolik Suite</h2>
          <p className="t-text-secondary text-sm">
            {isID
              ? 'Fitur ini eksklusif untuk anggota kategori Katholik Match. Silakan klaim profil Katholik Match terlebih dahulu.'
              : 'This feature is exclusive to Katholik Match members. Please claim a Katholik Match profile first.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen t-bg pb-24">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-3">
              <Sparkles className="w-3.5 h-3.5 text-[#D98A94]" />
              <span className="text-xs t-text-secondary font-medium">Katholik Match Exclusive</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold t-text font-romantic">Katolik Suite</h1>
          </div>
          <Link
            to="/kategori/katholik-match"
            data-testid="suite-stats-katolik"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#D98A94]/30 bg-[#D98A94]/10 hover:bg-[#D98A94]/20 text-[#C0727C] text-xs font-bold transition-colors"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            <span>{isID ? 'Statistik Komunitas' : 'Deep Stats'}</span>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
          {TABS.map(tb => {
            const isActive = tab === tb.key;
            return (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                data-testid={`katolik-tab-${tb.key}`}
                className={`p-3 sm:p-4 rounded-2xl border transition-all text-left ${isActive ? 'bg-[#D98A94] text-white border-[#D98A94] shadow-lg shadow-[#D98A94]/30' : 'glass-card t-text hover:border-[#D98A94]/50'}`}
              >
                <tb.icon className={`w-5 h-5 mb-1.5 ${isActive ? 'text-white' : 'text-[#D98A94]'}`} />
                <div className="text-xs sm:text-sm font-bold">{lang === 'en' ? tb.en : tb.id}</div>
                <div className={`text-[10px] mt-1 ${isActive ? 'text-white/85' : 't-text-muted'}`}>{lang === 'en' ? tb.desc_en : tb.desc_id}</div>
              </button>
            );
          })}
        </div>

        {tab === 'chat' && (
          <div>
            <PresencePanel authHeader={() => authHeader} lang={lang} />
            <div className="h-4" />
            <SuiteChatPanel API={API} scope="katolik" token={token} accountId={member?.id} lang={lang} />
          </div>
        )}
        {tab === 'meet' && <MeetSection API={API} authHeader={authHeader} lang={lang} member={member} scope="katolik" />}
        {tab === 'mart' && <ComingSoon label={isID ? 'Katolik Mart' : 'Katolik Mart'} lang={lang} />}
        {tab === 'mastery' && <MasterySection API={API} token={token} authHeader={authHeader} accountId={member?.id} lang={lang} scope="katolik" />}
        {tab === 'media' && <MediaSection API={API} token={token} authHeader={authHeader} accountId={member?.id} lang={lang} scope="katolik" />}
      </div>
      <BottomNav active="katolik" />
    </div>
  );
}
